import React, { FunctionComponent, useCallback, useContext, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { ImageSourcePropType, ScrollView, useWindowDimensions } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { format } from 'date-fns'
import { isEmpty, noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import {
  WELLNESS_CHECK_IN_ENTRY_POINTS,
  WELLNESS_CHECK_IN_ENTRY_POINTS_TYPE,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/mixpanelConstants'
import { PRODUCT_TYPE } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/productType'
import {
  TALK_WITH_PROVIDER_TAB,
  WELLNESS_CHECK_IN_RESULTS_DOMAIN_SCORE,
  WellnessCheckInDomain,
} from '@lyrahealth-inc/shared-app-logic/src/features/wellnessCheckIn/constants'
import {
  WellbeingResponse,
  WellnessCheckInResultsCostMessagingProps,
} from '@lyrahealth-inc/shared-app-logic/src/features/wellnessCheckIn/types'
import { getValidWellbeingResponse } from '@lyrahealth-inc/shared-app-logic/src/features/wellnessCheckIn/utils'

import wellnessCheckInResultsHeaderImage from '../../assets/WellnessCheckInOverviewIllustration.png'
import { BodyText } from '../../atoms/bodyText/BodyText'
import { Image } from '../../atoms/image/Image'
import { Link } from '../../atoms/link/Link'
import { Title } from '../../atoms/title/Title'
import { IS_WEB } from '../../constants'
import { useGetResponsiveAssets } from '../../hooks/useGetResponsiveAssets'
import { WellnessCheckInResultsEmptyState } from '../../molecules/wellnessCheckInResultsEmptyState/WellnessCheckInResultsEmptyState'
import { WellnessCheckInResultsPreviewOverlay } from '../../molecules/wellnessCheckInResultsPreviewOverlay/WellnessCheckInResultsPreviewOverlay'
import { WellnessCheckInResultsSocialNeeds } from '../../molecules/wellnessCheckInResultsSocialNeeds/WellnessCheckInResultsSocialNeeds'
import { WellnessCheckInResultsSubFooter } from '../../molecules/wellnessCheckInResultsSubFooter/WellnessCheckInResultsSubFooter'
import { WellnessCheckInResultsFullViewDomain } from '../../organisms/wellnessCheckInResultsFullViewDomain/WellnessCheckInResultsFullViewDomain'
import { WellnessCheckInResultsMobileViewDomain } from '../../organisms/wellnessCheckInResultsMobileViewDomain/WellnessCheckInResultsMobileViewDomain'
import { BodyTextSize, TitleSize } from '../../styles/typeStyles'
import { Flex1SafeAreaView } from '../../templates/content/CommonViews'
import { ProductTypeContext } from '../../utils/productType/productTypeContext'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { translateWellnessCheckInNumberScoreToDomainScore } from '../../utils/wellnessCheckInResults/utils'

export interface WellnessCheckInResultsProps {
  wellbeingResponse?: WellbeingResponse | null
  onSubFooterCTAPress?: () => void
  onLearnMorePress?: () => void
  isSocialNeedEnabled: boolean
  onArticlePress?: (subPath: string) => void
  onEmptyStatePress?: () => void
  isPreview?: boolean
  previewComponents?: React.ReactElement
  navBarComponent?: React.ReactElement
  footerComponents?: React.ReactElement
  onDomainPress?: (domain: WellnessCheckInDomain) => void
  onLoad?: () => void
  onEmptyStateLoad?: () => void
  entryPoint?: WELLNESS_CHECK_IN_ENTRY_POINTS_TYPE
  resultsDate?: string
  onRestartPress?: () => void
  onProviderTabCardPress?: () => void
  costMessagingProps?: WellnessCheckInResultsCostMessagingProps
}

const HeaderContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  paddingBottom: theme.breakpoints.isMobileSized ? theme.spacing['56px'] : theme.spacing['48px'],
  alignItems: 'center',
}))

const BackgroundImageHeaderContainer = styled(Image)<{ theme: ThemeType; width?: number; top?: number }>(
  ({ theme, width = 0, top = 0 }) => ({
    width: '100%',
    height: '100%',
    position: 'absolute',
    ...(!theme.breakpoints.isMinWidthMobileL && {
      width: '600px',
      height: `${336 + top}px`,
      left: `-${(600 - width) / 2}px`,
    }),
  }),
)

const ImageContainer = styled(Image)<{ theme: ThemeType }>(({ theme }) => ({
  width: theme.breakpoints.isMobileSized ? '112px' : '94px',
  height: theme.breakpoints.isMobileSized ? '112px' : '94px',
  margin: 'auto',
  ...(IS_WEB && {
    marginTop: '68px', // add margin top to account for LW header
  }),
}))

const TitleContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginTop: theme.spacing['24px'],
  marginBottom: theme.spacing['8px'],
}))

const ScrollViewContainer = styled(ScrollView)<{ isPreview: boolean; windowHeight: number }>(
  ({ isPreview, windowHeight }) => ({
    ...(isPreview && { maxHeight: 0.85 * windowHeight, overflow: 'hidden' }),
  }),
)

const Container = styled.View(({ theme }) => ({
  height: '100%',
  backgroundColor: theme.colors.backgroundPrimary,
}))

export const WellnessCheckInResults: FunctionComponent<WellnessCheckInResultsProps> = ({
  wellbeingResponse,
  onSubFooterCTAPress = noop,
  onLearnMorePress = noop,
  isSocialNeedEnabled,
  onArticlePress = noop,
  onEmptyStatePress = noop,
  isPreview = false,
  previewComponents,
  navBarComponent,
  footerComponents,
  onDomainPress,
  onLoad = noop,
  onEmptyStateLoad = noop,
  entryPoint,
  resultsDate,
  onRestartPress = noop,
  onProviderTabCardPress,
  costMessagingProps,
}) => {
  const {
    breakpoints: { isMinWidthMobileL, isMobileSized },
    assets: { wellnessCheckInResultsHeader },
  } = useTheme() as ThemeType
  const { productType } = useContext(ProductTypeContext)
  const { height, width } = useWindowDimensions()
  const { top } = useSafeAreaInsets()
  const backgroundImage = useGetResponsiveAssets(wellnessCheckInResultsHeader.assetNames)
  const validWellbeingResponse = wellbeingResponse ? getValidWellbeingResponse(wellbeingResponse) : null
  const isInvalidWellbeingResponse = !validWellbeingResponse || isEmpty(validWellbeingResponse)

  const { domainScores, lowScoreDomains } = useMemo(() => {
    if (!validWellbeingResponse) {
      return { domainScores: {}, lowScoreDomains: [] }
    }

    const lowDomains: WellnessCheckInDomain[] = []
    const scores = Object.keys(validWellbeingResponse).reduce((acc, domain) => {
      const score = translateWellnessCheckInNumberScoreToDomainScore(
        validWellbeingResponse[domain as WellnessCheckInDomain].score,
      )
      if (score === WELLNESS_CHECK_IN_RESULTS_DOMAIN_SCORE.LOW) {
        lowDomains.push(domain as WellnessCheckInDomain)
      }
      acc[domain] = score
      return acc
    }, {} as Record<string, WELLNESS_CHECK_IN_RESULTS_DOMAIN_SCORE>)

    return { domainScores: scores, lowScoreDomains: lowDomains }
  }, [validWellbeingResponse])

  const emptyStateComponent = (
    <>
      <WellnessCheckInResultsEmptyState onEmptyStatePress={onEmptyStatePress} onEmptyStateLoad={onEmptyStateLoad} />
      {footerComponents && <>{footerComponents}</>}
    </>
  )

  if (!wellbeingResponse || isInvalidWellbeingResponse) {
    return emptyStateComponent
  }

  const showResultsDate = !isPreview && !!resultsDate && entryPoint !== WELLNESS_CHECK_IN_ENTRY_POINTS.ASSESSMENT

  const getHeaderImage = () => {
    return (
      <BackgroundImageHeaderContainer
        source={backgroundImage as ImageSourcePropType}
        contentFit='fill'
        accessibilityIgnoresInvertColors
        width={!isMinWidthMobileL ? width : undefined}
        top={!isMinWidthMobileL ? top : undefined}
      />
    )
  }

  const getTabKeys = useCallback(() => {
    const domainKeys = Object.keys(domainScores)
    if (productType === PRODUCT_TYPE.WFMH && !isPreview) {
      return [...domainKeys, TALK_WITH_PROVIDER_TAB]
    }
    return domainKeys
  }, [domainScores, isPreview, productType])

  const contentViewProps = {
    wellbeingResponse: validWellbeingResponse,
    tabKeys: getTabKeys(),
    domainScores,
    lowScoreDomains,
    onArticlePress,
    isPreview,
    onDomainPress,
    onLoad,
    onProviderTabCardPress,
    costMessagingProps,
  }

  return (
    <Container>
      {isPreview && previewComponents && (
        <WellnessCheckInResultsPreviewOverlay>{previewComponents}</WellnessCheckInResultsPreviewOverlay>
      )}
      <ScrollViewContainer
        testID={tID(`WellnessCheckInResults`)}
        scrollEnabled={!isPreview}
        nestedScrollEnabled={!isPreview}
        isPreview={isPreview}
        pointerEvents={isPreview ? 'none' : undefined}
        windowHeight={height}
      >
        <HeaderContainer>
          {getHeaderImage()}
          <Flex1SafeAreaView edges={['top']} />
          {navBarComponent}
          <ImageContainer
            source={wellnessCheckInResultsHeaderImage as ImageSourcePropType}
            contentFit='cover'
            accessibilityIgnoresInvertColors
          />
          <TitleContainer>
            <Title
              level='2'
              size={TitleSize.LARGE}
              text={
                <FormattedMessage
                  defaultMessage='Your wellness results'
                  description='Title for wellness check in result header section'
                />
              }
              textAlign='center'
            />
          </TitleContainer>
          <BodyText
            text={
              <FormattedMessage
                defaultMessage='Crafted by Lyra’s clinical experts{showResultsDate, select, true {, from {date}} other {}}'
                description='Description for wellness check in result header section'
                values={{
                  showResultsDate,
                  date: resultsDate ? format(new Date(resultsDate), 'MMM d, yyyy') : '',
                }}
              />
            }
            size={BodyTextSize.DEFAULT}
            textAlign='center'
          />
          {!isPreview && (
            <BodyText
              text={
                <FormattedMessage
                  defaultMessage='<link>Check in again</link>'
                  description='Link that takes user to retake the wellness check-in form'
                  values={{
                    link: (chunks: string) => (
                      <BodyText>
                        <Link
                          testID={tID('WellnessCheckInResults-CheckInAgain')}
                          text={chunks}
                          onPress={onRestartPress}
                          underline
                        />
                      </BodyText>
                    ),
                  }}
                />
              }
              size={BodyTextSize.DEFAULT}
              textAlign='center'
            />
          )}
        </HeaderContainer>
        {isMobileSized ? (
          <WellnessCheckInResultsMobileViewDomain {...contentViewProps} />
        ) : (
          <WellnessCheckInResultsFullViewDomain {...contentViewProps} />
        )}
        <WellnessCheckInResultsSubFooter onCTAPress={onSubFooterCTAPress} />
        {isSocialNeedEnabled && <WellnessCheckInResultsSocialNeeds onPress={onLearnMorePress} />}
      </ScrollViewContainer>
      {!isPreview && footerComponents && <>{footerComponents}</>}
    </Container>
  )
}
